import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { createLogger } from '../utils/logger';
import { apiFetch } from '../utils/api';
import { fetchLiveJobs } from '../hooks/useJobs';

const log = createLogger('GeneratePage');

interface SubmissionResult {
  scan_id: string;
  job_id?: string;
  status: 'pending' | 'submitted' | 'error';
  error?: string;
}

type LicensePlateOption = 'no_plate' | 'bracket_only' | 'keep_plate' | 'custom_plate';

const LP_OPTIONS: { value: LicensePlateOption; label: string }[] = [
  { value: 'no_plate', label: 'No plate' },
  { value: 'bracket_only', label: 'Bracket only' },
  { value: 'keep_plate', label: 'Keep plate' },
  { value: 'custom_plate', label: 'Custom plate' },
];

type GenerationTierOption = 'tier0' | 'tier1' | 'tier2' | 'tier3' | 'tier4';

// The resolution names what a tier renders at, not what it returns: every tier
// is delivered at 1600x1200, so a tier trades cost against detail only.
// Tier 0 is the exception: it swaps the engine for Meta Muse Image, which sizes
// its own output.
const GENERATION_TIER_OPTIONS: { value: GenerationTierOption; label: string }[] = [
  { value: 'tier0', label: 'Tier 0 — Muse Image' },
  { value: 'tier1', label: 'Tier 1 — Sunburst, 1K render / medium' },
  { value: 'tier2', label: 'Tier 2 — Sunburst, 2K render / medium' },
  { value: 'tier3', label: 'Tier 3 — Sunburst, 2K render / high' },
  { value: 'tier4', label: 'Tier 4 — Sunburst, 2K render / high + Sol 6.1 analysis' },
];
const GENERATION_TIER_HELP =
  'All tiers are delivered at 1600 × 1200. Tiers 1-4 use GPT Image 2.5 Sunburst; Tier 0 uses Muse Image. Only Tier 4 runs the Sol 6.1 exterior analysis.';

async function findAcceptedLiveJob(
  scanId: string,
  generationTier: GenerationTierOption,
  submittedAt: number,
) {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    try {
      const jobs = await fetchLiveJobs();
      const acceptedJob = jobs
        .filter((job) => (
          job.scan_id === scanId
          && job.source === 'manual'
          && job.generation_tier === generationTier
          && Date.parse(job.created_at) >= submittedAt - 5_000
        ))
        .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0];

      if (acceptedJob) return acceptedJob;
    } catch (error) {
      log.warn('Could not reconcile ambiguous scan submission:', error);
    }
  }

  return null;
}

// Pre-known pickup bed/cabin configuration ('' = not specified -> backend infers
// nothing and leaves it out of the prompt).
type BedTypeOption = '' | 'short' | 'standard' | 'long';
type CabinTypeOption =
  | ''
  | 'regular'
  | 'supercab'
  | 'supercrew'
  | 'double'
  | 'crew'
  | 'quad'
  | 'super'
  | 'mega';

const BED_OPTIONS: { value: BedTypeOption; label: string }[] = [
  { value: '', label: 'Not specified' },
  { value: 'short', label: 'Short bed' },
  { value: 'standard', label: 'Standard bed' },
  { value: 'long', label: 'Long bed' },
];

const CABIN_OPTIONS: { value: CabinTypeOption; label: string }[] = [
  { value: '', label: 'Not specified' },
  { value: 'regular', label: 'Regular' },
  { value: 'supercab', label: 'SuperCab' },
  { value: 'supercrew', label: 'SuperCrew' },
  { value: 'double', label: 'Double' },
  { value: 'crew', label: 'Crew' },
  { value: 'quad', label: 'Quad' },
  { value: 'super', label: 'Super' },
  { value: 'mega', label: 'Mega' },
];

// Batch CSV column parsing. Optional per-scan values override the controls
// below for that row. Values are matched case-insensitively against the closed
// vocabularies; unrecognized cells are ignored.
const SCAN_ID_HEADERS = ['scan_id', 'scanid', 'id', 'scan', 'inspection_id', 'inspectionid'];
const BED_COLUMN = 'pickup_bed';
const CABIN_COLUMN = 'pickup_cabin';
const FRONT_PLATE_COLUMN = 'front_plate';
const REAR_PLATE_COLUMN = 'rear_plate';
const PLATE_NAME_COLUMN = 'plate_name';
const BACKGROUND_COLUMNS = ['background', 'photobooth'];
const BED_TOKENS = new Set<string>(BED_OPTIONS.map((o) => o.value).filter(Boolean));
const CABIN_TOKENS = new Set<string>(CABIN_OPTIONS.map((o) => o.value).filter(Boolean));
const PLATE_TOKENS = new Set<string>(LP_OPTIONS.map((option) => option.value));

type BatchScanSettings = {
  bed_type?: string;
  cabin_type?: string;
  license_plate?: {
    front?: LicensePlateOption;
    rear?: LicensePlateOption;
    plate_name?: string;
  };
};

type BatchCsvRow = {
  scanId: string;
  photobooth?: string;
  settings: BatchScanSettings;
};

export function GeneratePage() {
  // Single scan state
  const [singleScanId, setSingleScanId] = useState('');
  const [singleSubmitting, setSingleSubmitting] = useState(false);
  const [singleResult, setSingleResult] = useState<SubmissionResult | null>(null);

  // Single scan settings
  const [photoboothName, setPhotoboothName] = useState('');
  const [lpFront, setLpFront] = useState<LicensePlateOption>('no_plate');
  const [lpRear, setLpRear] = useState<LicensePlateOption>('bracket_only');
  const [plateName, setPlateName] = useState('');
  const [highReflectance, setHighReflectance] = useState(false);
  const [metadataFree, setMetadataFree] = useState(false);
  const [useReferenceBank, setUseReferenceBank] = useState(false);
  const [generationTier, setGenerationTier] = useState<GenerationTierOption>('tier3');
  // Pre-known pickup bed/cabin (only sent when not 'Not specified')
  const [bedType, setBedType] = useState<BedTypeOption>('');
  const [cabinType, setCabinType] = useState<CabinTypeOption>('');

  // Batch upload state
  const [batchFileName, setBatchFileName] = useState<string | null>(null);
  const [batchScanIds, setBatchScanIds] = useState<string[]>([]);
  const [batchRows, setBatchRows] = useState<BatchCsvRow[]>([]);
  const [batchResults, setBatchResults] = useState<SubmissionResult[]>([]);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Batch settings
  const [batchPhotoboothName, setBatchPhotoboothName] = useState('');
  const [batchLpFront, setBatchLpFront] = useState<LicensePlateOption>('no_plate');
  const [batchLpRear, setBatchLpRear] = useState<LicensePlateOption>('bracket_only');
  const [batchPlateName, setBatchPlateName] = useState('');
  const [batchHighReflectance, setBatchHighReflectance] = useState(false);
  const [batchMetadataFree, setBatchMetadataFree] = useState(false);
  const [batchUseReferenceBank, setBatchUseReferenceBank] = useState(false);
  const [batchGenerationTier, setBatchGenerationTier] = useState<GenerationTierOption>('tier3');

  // Shared photobooth list
  const [photoboothNames, setPhotoboothNames] = useState<string[]>([]);
  const [plateNames, setPlateNames] = useState<string[]>([]);

  // Backend defaults for license plate (used to decide when to send settings)
  const [lpDefaults, setLpDefaults] = useState<{ front: LicensePlateOption; rear: LicensePlateOption }>({ front: 'no_plate', rear: 'bracket_only' });
  // Backend default for high reflectance (used to decide when to send settings)
  const [hrDefault, setHrDefault] = useState(false);
  // Backend default for metadata free mode (used to decide when to send settings)
  const [mfDefault, setMfDefault] = useState(false);
  const [rbDefault, setRbDefault] = useState(false);

  useEffect(() => {
    apiFetch('/api/settings/photobooths')
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setPhotoboothNames(
            Array.isArray(data) ? data.map((p: { name: string }) => p.name) : []
          );
        }
      })
      .catch((err) => log.error('Failed to fetch photobooths:', err));

    apiFetch('/api/settings/plates')
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setPlateNames(
            Array.isArray(data) ? data.map((plate: { name: string }) => plate.name) : []
          );
        }
      })
      .catch((err) => log.error('Failed to fetch custom plates:', err));

    apiFetch('/api/settings/scan-defaults')
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          const front = (data?.license_plate?.front ?? 'no_plate') as LicensePlateOption;
          const rear = (data?.license_plate?.rear ?? 'bracket_only') as LicensePlateOption;
          setLpFront(front); setBatchLpFront(front);
          setLpRear(rear); setBatchLpRear(rear);
          setLpDefaults({ front, rear });
          const hr = Boolean(data?.high_reflectance ?? false);
          setHighReflectance(hr); setBatchHighReflectance(hr);
          setHrDefault(hr);
          const mf = Boolean(data?.metadata_free ?? false);
          setMetadataFree(mf); setBatchMetadataFree(mf);
          setMfDefault(mf);
          const rb = Boolean(data?.use_reference_bank ?? false);
          setUseReferenceBank(rb); setBatchUseReferenceBank(rb);
          setRbDefault(rb);
          const tier = data?.generation_tier as GenerationTierOption;
          if (GENERATION_TIER_OPTIONS.some((option) => option.value === tier)) {
            setGenerationTier(tier);
            setBatchGenerationTier(tier);
          }
        }
      })
      .catch((err) => log.error('Failed to fetch scan defaults:', err));
  }, []);

  // Submit a single scan
  const submitSingleScan = async () => {
    const scanId = singleScanId.trim();
    if (!scanId) return;
    const usesCustomPlate = lpFront === 'custom_plate' || lpRear === 'custom_plate';
    if (usesCustomPlate && !plateName) {
      setSingleResult({
        scan_id: scanId,
        status: 'error',
        error: 'Select a custom plate before submitting.',
      });
      return;
    }

    setSingleSubmitting(true);
    setSingleResult(null);
    const submittedAt = Date.now();

    try {
      const payload: Record<string, unknown> = {
        scan_id: scanId,
        source: 'manual',
      };
      if (photoboothName) payload.photobooth_name = photoboothName;
      const hasLpSettings = (
        lpFront !== lpDefaults.front
        || lpRear !== lpDefaults.rear
        || usesCustomPlate
      );
      const hasHrSettings = highReflectance !== hrDefault;
      const hasMfSettings = metadataFree !== mfDefault;
      const hasRbSettings = useReferenceBank !== rbDefault;
      const hasBedCabin = bedType !== '' || cabinType !== '';
      payload.settings = {
        generation_tier: generationTier,
        ...(hasLpSettings ? {
          license_plate: {
            front: lpFront,
            rear: lpRear,
            ...(usesCustomPlate ? { plate_name: plateName } : {}),
          },
        } : {}),
        ...(hasHrSettings ? { high_reflectance: highReflectance } : {}),
        ...(hasMfSettings ? { metadata_free: metadataFree } : {}),
        ...(hasRbSettings ? { use_reference_bank: useReferenceBank } : {}),
        ...(hasBedCabin && bedType ? { bed_type: bedType } : {}),
        ...(hasBedCabin && cabinType ? { cabin_type: cabinType } : {}),
      };

      const response = await apiFetch('/scans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        log.info(`Single scan submitted: scan_id=${scanId}, job_id=${data.job_id}`);
        setSingleResult({
          scan_id: scanId,
          job_id: data.job_id,
          status: 'submitted',
        });
        setSingleScanId('');
      } else {
        const error = await response.text();
        log.error(`Single scan submission failed: scan_id=${scanId}, status=${response.status}`, error);
        setSingleResult({
          scan_id: scanId,
          status: 'error',
          error: error || 'Failed to submit scan',
        });
      }
    } catch (err) {
      log.error('Single scan submission network error:', err);
      const acceptedJob = await findAcceptedLiveJob(
        scanId,
        generationTier,
        submittedAt,
      );
      if (acceptedJob) {
        log.info(`Recovered accepted scan submission: scan_id=${scanId}, job_id=${acceptedJob.job_id}`);
        setSingleResult({
          scan_id: scanId,
          job_id: acceptedJob.job_id,
          status: 'submitted',
        });
        setSingleScanId('');
        return;
      }

      setSingleResult({
        scan_id: scanId,
        status: 'error',
        error: err instanceof Error ? err.message : 'Network error',
      });
    } finally {
      setSingleSubmitting(false);
    }
  };

  // Parse CSV file. The scan-id column is required; optional background,
  // pickup, and plate columns become per-scan overrides. Files without a
  // header fall back to first-column-only.
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBatchFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) return;

      const splitRow = (line: string) => line.split(',').map((part) => part.trim());
      const isValidScanId = (id: string) =>
        /^[a-zA-Z0-9-_]+$/.test(id) && id.length > 5 && !SCAN_ID_HEADERS.includes(id.toLowerCase());

      const lines = text.split(/[\r\n]+/).filter((line) => line.trim());
      if (lines.length === 0) {
        setBatchScanIds([]);
        setBatchRows([]);
        setBatchResults([]);
        return;
      }

      // Detect an (optional) header row so we can locate columns by name.
      const header = splitRow(lines[0]).map((h) => h.toLowerCase());
      let scanCol = header.findIndex((h) => SCAN_ID_HEADERS.includes(h));
      const bedCol = header.indexOf(BED_COLUMN);
      const cabinCol = header.indexOf(CABIN_COLUMN);
      const frontPlateCol = header.indexOf(FRONT_PLATE_COLUMN);
      const rearPlateCol = header.indexOf(REAR_PLATE_COLUMN);
      const plateNameCol = header.indexOf(PLATE_NAME_COLUMN);
      const backgroundCol = header.findIndex((h) => BACKGROUND_COLUMNS.includes(h));
      const hasHeader = (
        scanCol >= 0
        || bedCol >= 0
        || cabinCol >= 0
        || frontPlateCol >= 0
        || rearPlateCol >= 0
        || plateNameCol >= 0
        || backgroundCol >= 0
      );
      if (scanCol < 0) scanCol = 0;
      const dataLines = hasHeader ? lines.slice(1) : lines;

      const validIds: string[] = [];
      const parsedRows: BatchCsvRow[] = [];
      for (const line of dataLines) {
        const parts = splitRow(line);
        const id = parts[scanCol];
        if (!id || !isValidScanId(id)) continue;
        validIds.push(id);

        const scanSettings: BatchScanSettings = {};
        const photobooth = backgroundCol >= 0
          ? (parts[backgroundCol] || '')
          : '';
        if (bedCol >= 0) {
          const bed = (parts[bedCol] || '').toLowerCase();
          if (BED_TOKENS.has(bed)) scanSettings.bed_type = bed;
        }
        if (cabinCol >= 0) {
          const cabin = (parts[cabinCol] || '').toLowerCase();
          if (CABIN_TOKENS.has(cabin)) scanSettings.cabin_type = cabin;
        }
        const frontPlate = (parts[frontPlateCol] || '').toLowerCase();
        const rearPlate = (parts[rearPlateCol] || '').toLowerCase();
        if (PLATE_TOKENS.has(frontPlate) || PLATE_TOKENS.has(rearPlate)) {
          scanSettings.license_plate = {};
          if (PLATE_TOKENS.has(frontPlate)) {
            scanSettings.license_plate.front = frontPlate as LicensePlateOption;
          }
          if (PLATE_TOKENS.has(rearPlate)) {
            scanSettings.license_plate.rear = rearPlate as LicensePlateOption;
          }
        }
        if (plateNameCol >= 0) {
          const plateName = parts[plateNameCol] || '';
          if (plateName) {
            scanSettings.license_plate ??= {};
            scanSettings.license_plate.plate_name = plateName;
          }
        }
        parsedRows.push({
          scanId: id,
          ...(photobooth ? { photobooth } : {}),
          settings: scanSettings,
        });
      }

      setBatchScanIds(validIds);
      setBatchRows(parsedRows);
      setBatchResults([]);
    };

    reader.readAsText(file);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Submit batch scans via batch API
  const submitBatchScans = async () => {
    if (batchScanIds.length === 0 || !batchFileName) return;
    const rowsMissingCustomPlate = batchRows.filter((row) => {
      const plateOverride = row.settings.license_plate;
      const front = plateOverride?.front ?? batchLpFront;
      const rear = plateOverride?.rear ?? batchLpRear;
      const usesCustomPlate = (
        front === 'custom_plate' || rear === 'custom_plate'
      );
      const plateName = plateOverride?.plate_name || batchPlateName;
      return usesCustomPlate && !plateName;
    });
    if (rowsMissingCustomPlate.length > 0) {
      const missingRows = new Set(rowsMissingCustomPlate);
      setBatchResults(batchRows.map((row) => ({
        scan_id: row.scanId,
        status: 'error' as const,
        error: missingRows.has(row)
          ? 'Select a custom plate or provide plate_name in the CSV.'
          : 'Batch was not submitted because another row is missing a custom plate.',
      })));
      return;
    }

    setBatchSubmitting(true);

    try {
      const batchPayload: Record<string, unknown> = {
        filename: batchFileName,
        scan_requests: batchRows.map((row) => ({
          scan_id: row.scanId,
          ...(row.photobooth ? { photobooth: row.photobooth } : {}),
          ...(Object.keys(row.settings).length > 0
            ? { settings: row.settings }
            : {}),
        })),
      };
      if (batchPhotoboothName) batchPayload.photobooth = batchPhotoboothName;
      const hasBatchLpSettings = (
        batchLpFront !== lpDefaults.front
        || batchLpRear !== lpDefaults.rear
        || Boolean(batchPlateName)
      );
      const hasBatchHrSettings = batchHighReflectance !== hrDefault;
      const hasBatchMfSettings = batchMetadataFree !== mfDefault;
      const hasBatchRbSettings = batchUseReferenceBank !== rbDefault;
      batchPayload.settings = {
        generation_tier: batchGenerationTier,
        ...(hasBatchLpSettings ? {
          license_plate: {
            front: batchLpFront,
            rear: batchLpRear,
            ...(batchPlateName ? { plate_name: batchPlateName } : {}),
          },
        } : {}),
        ...(hasBatchHrSettings ? { high_reflectance: batchHighReflectance } : {}),
        ...(hasBatchMfSettings ? { metadata_free: batchMetadataFree } : {}),
        ...(hasBatchRbSettings ? { use_reference_bank: batchUseReferenceBank } : {}),
      };
      const response = await apiFetch('/api/batches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batchPayload),
      });

      if (response.ok) {
        const data = await response.json();
        log.info(`Batch submitted: batch_id=${data.batch_id}, scans=${batchScanIds.length}, filename=${batchFileName}`);
        // Mark all as submitted
        const results: SubmissionResult[] = batchScanIds.map(scanId => ({
          scan_id: scanId,
          job_id: data.batch_id,
          status: 'submitted' as const,
        }));
        setBatchResults(results);
      } else {
        const error = await response.text();
        log.error(`Batch submission failed: status=${response.status}, scans=${batchScanIds.length}`, error);
        // Mark all as error
        const results: SubmissionResult[] = batchScanIds.map(scanId => ({
          scan_id: scanId,
          status: 'error' as const,
          error: error || 'Failed to submit batch',
        }));
        setBatchResults(results);
      }
    } catch (err) {
      log.error('Batch submission network error:', err);
      const errorMsg = err instanceof Error ? err.message : 'Network error';
      const results: SubmissionResult[] = batchScanIds.map(scanId => ({
        scan_id: scanId,
        status: 'error' as const,
        error: errorMsg,
      }));
      setBatchResults(results);
    } finally {
      setBatchSubmitting(false);
    }
  };

  // Clear batch
  const clearBatch = () => {
    setBatchFileName(null);
    setBatchScanIds([]);
    setBatchRows([]);
    setBatchResults([]);
  };

  const successCount = batchResults.filter(r => r.status === 'submitted').length;
  const errorCount = batchResults.filter(r => r.status === 'error').length;
  const configuredBatchRowCount = batchRows.filter(
    (row) => Boolean(row.photobooth) || Object.keys(row.settings).length > 0,
  ).length;
  const hasCsvCustomPlate = batchRows.some(
    (row) => (
      row.settings.license_plate?.front === 'custom_plate'
      || row.settings.license_plate?.rear === 'custom_plate'
    ),
  );
  const hasMissingBatchPlate = batchRows.some((row) => {
    const plateOverride = row.settings.license_plate;
    const front = plateOverride?.front ?? batchLpFront;
    const rear = plateOverride?.rear ?? batchLpRear;
    const usesCustomPlate = (
      front === 'custom_plate' || rear === 'custom_plate'
    );
    return (
      usesCustomPlate
      && !(plateOverride?.plate_name || batchPlateName)
    );
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Generate Merch Images</h1>
        <p className="mt-1 text-sm text-slate-500">
          Submit one scan or upload a batch for image generation.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Single Scan Section */}
        <section className="bg-white rounded-xl border border-slate-200 shadow-soft p-4 sm:p-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Single Scan</h2>
            <p className="text-sm text-slate-500">Generate images for one scan ID</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="scanId" className="block text-sm font-medium text-slate-700 mb-2">
              Scan ID
            </label>
            <input
              id="scanId"
              type="text"
              value={singleScanId}
              onChange={(e) => setSingleScanId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitSingleScan()}
              placeholder="e.g., 57942aad-a26d-4b05-8c1b-7de5aa32326d"
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 font-mono text-sm transition-all duration-200"
            />
          </div>

          {/* Settings */}
          <fieldset className="space-y-3 rounded-xl border border-slate-200 p-3">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Generation options
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
              <label htmlFor="photobooth" className="block text-xs font-medium text-slate-600 mb-1">Background</label>
              <select
                id="photobooth"
                value={photoboothName}
                onChange={(e) => setPhotoboothName(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                <option value="">Server default</option>
                {photoboothNames.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              </div>
              <div>
              <label htmlFor="lpFront" className="block text-xs font-medium text-slate-600 mb-1">Plate (Front)</label>
              <select
                id="lpFront"
                value={lpFront}
                onChange={(e) => setLpFront(e.target.value as LicensePlateOption)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                {LP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              </div>
              <div>
              <label htmlFor="lpRear" className="block text-xs font-medium text-slate-600 mb-1">Plate (Rear)</label>
              <select
                id="lpRear"
                value={lpRear}
                onChange={(e) => setLpRear(e.target.value as LicensePlateOption)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                {LP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              </div>
              {(lpFront === 'custom_plate' || lpRear === 'custom_plate') && (
                <div className="sm:col-span-2">
                  <label htmlFor="plateName" className="block text-xs font-medium text-slate-600 mb-1">
                    Custom plate
                  </label>
                  <select
                    id="plateName"
                    value={plateName}
                    onChange={(e) => setPlateName(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                  >
                    <option value="">Select a stored plate</option>
                    {plateNames.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  {plateNames.length === 0 && (
                    <p className="mt-1 text-[11px] text-amber-700">
                      Upload a plate image from Settings before using this mode.
                    </p>
                  )}
                </div>
              )}
              <div>
              <label htmlFor="generationTier" className="block text-xs font-medium text-slate-600 mb-1">Generation tier</label>
              <select
                id="generationTier"
                aria-describedby="generation-tier-help"
                value={generationTier}
                onChange={(e) => setGenerationTier(e.target.value as GenerationTierOption)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                {GENERATION_TIER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p id="generation-tier-help" className="mt-1.5 text-[11px] leading-4 text-slate-500">
                {GENERATION_TIER_HELP}
              </p>
              </div>
            </div>

            {/* High reflectance toggle */}
            <label htmlFor="highReflectance" className="flex items-center gap-2 cursor-pointer select-none">
              <input
                id="highReflectance"
                type="checkbox"
                checked={highReflectance}
                onChange={(e) => setHighReflectance(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-xs font-medium text-slate-600">
                High reflectance (outdoor-style reflections)
              </span>
            </label>

            {/* Metadata free mode toggle */}
            <label htmlFor="metadataFree" className="flex items-center gap-2 cursor-pointer select-none">
              <input
                id="metadataFree"
                type="checkbox"
                checked={metadataFree}
                onChange={(e) => setMetadataFree(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-xs font-medium text-slate-600">
                Metadata free mode (rely on reference images only)
              </span>
            </label>

            <label htmlFor="useReferenceBank" className="flex items-center gap-2 cursor-pointer select-none">
              <input
                id="useReferenceBank"
                type="checkbox"
                checked={useReferenceBank}
                onChange={(e) => setUseReferenceBank(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-xs font-medium text-slate-600">
                Use reference bank (when make, model, year and trim match an entry)
              </span>
            </label>

            {/* Pre-known pickup bed/cabin (optional; only for pickup trucks) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="bedType" className="block text-xs font-medium text-slate-600 mb-1">Pickup bed</label>
                <select
                  id="bedType"
                  value={bedType}
                  onChange={(e) => setBedType(e.target.value as BedTypeOption)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  {BED_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="cabinType" className="block text-xs font-medium text-slate-600 mb-1">Pickup cabin</label>
                <select
                  id="cabinType"
                  value={cabinType}
                  onChange={(e) => setCabinType(e.target.value as CabinTypeOption)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  {CABIN_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </fieldset>

          <button
            onClick={submitSingleScan}
            disabled={
              !singleScanId.trim()
              || singleSubmitting
              || ((lpFront === 'custom_plate' || lpRear === 'custom_plate') && !plateName)
            }
            className="btn-primary w-full px-4 py-2.5 text-sm flex items-center justify-center gap-2"
          >
            {singleSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate
              </>
            )}
          </button>

          <p className="sr-only" aria-live="polite" aria-atomic="true">
            {singleSubmitting
              ? 'Submitting scan.'
              : singleResult?.status === 'submitted'
                ? 'Scan submitted successfully.'
                : singleResult?.status === 'error'
                  ? `Failed to submit scan. ${singleResult.error ?? ''}`
                  : ''}
          </p>

          {/* Single scan result */}
          {singleResult && (
            <div className={`p-4 rounded-lg ${
              singleResult.status === 'submitted'
                ? 'bg-success-50 text-success-700'
                : 'bg-danger-50 text-danger-700'
            }`}>
              {singleResult.status === 'submitted' ? (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium">Scan submitted successfully</p>
                    <p className="text-xs font-mono mt-1 text-slate-600">Job ID: {singleResult.job_id}</p>
                    <Link
                      to="/lightning/live"
                      className="mt-2 inline-flex text-xs font-medium text-indigo-700 underline underline-offset-2 hover:text-indigo-900"
                    >
                      View in Live Scans
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium">Failed to submit scan</p>
                    <p className="text-xs text-slate-600 mt-1">{singleResult.error}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        </section>

      {/* Batch Upload Section */}
        <section className="bg-white rounded-xl border border-slate-200 shadow-soft p-4 sm:p-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Batch Upload</h2>
            <p className="text-sm text-slate-500">Generate images from a CSV file</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* File upload area */}
          <div>
            <input
              id="batch-file"
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileUpload}
              aria-describedby="batch-file-help batch-file-columns"
              className="sr-only"
            />
            <label
              htmlFor="batch-file"
              className="block cursor-pointer rounded-xl border-2 border-dashed border-slate-200 p-6 text-center transition-all duration-200 hover:border-indigo-400 hover:bg-indigo-50/30 focus-within:border-indigo-500"
            >
              <svg className="w-12 h-12 mx-auto text-slate-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-slate-700">
                Choose a CSV file or <span className="text-indigo-600 font-medium">browse</span>
              </p>
              <p id="batch-file-help" className="text-xs text-slate-500 mt-1">
                One scan ID per line, or use a header row with a scan ID column.
              </p>
              <p id="batch-file-columns" className="mt-2 text-[11px] leading-4 text-slate-500">
                Optional columns: <code>background</code>, <code>front_plate</code>, <code>rear_plate</code>,{' '}
                <code>plate_name</code>, <code>pickup_bed</code>, and <code>pickup_cabin</code>.
                Plate modes: no_plate, bracket_only, keep_plate, custom_plate.
              </p>
            </label>
          </div>

          {/* Scan IDs preview */}
          {batchScanIds.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">
                    <span className="text-slate-900 font-semibold">{batchRows.length}</span> CSV rows loaded
                    {configuredBatchRowCount > 0 && (
                      <span className="text-slate-500">
                        {' '}· <span className="font-semibold">{configuredBatchRowCount}</span> with per-line settings
                      </span>
                    )}
                  </span>
                  {batchFileName && (
                    <span className="text-xs text-slate-400 font-mono">({batchFileName})</span>
                  )}
                </div>
                <button
                  onClick={clearBatch}
                  className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Clear all
                </button>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {batchScanIds.slice(0, 10).map((id, idx) => (
                  <div key={idx} className="text-xs font-mono text-slate-500 truncate">
                    {id}
                  </div>
                ))}
                {batchScanIds.length > 10 && (
                  <div className="text-xs text-slate-400">
                    ... and {batchScanIds.length - 10} more
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Batch settings */}
          {batchScanIds.length > 0 && (
            <fieldset className="space-y-3 rounded-xl border border-slate-200 p-3">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Generation options
              </legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                <label htmlFor="batchPhotobooth" className="block text-xs font-medium text-slate-600 mb-1">Background</label>
                <select
                  id="batchPhotobooth"
                  value={batchPhotoboothName}
                  onChange={(e) => setBatchPhotoboothName(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  <option value="">Server default</option>
                  {photoboothNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                </div>
                <div>
                <label htmlFor="batchLpFront" className="block text-xs font-medium text-slate-600 mb-1">Plate (Front)</label>
                <select
                  id="batchLpFront"
                  value={batchLpFront}
                  onChange={(e) => setBatchLpFront(e.target.value as LicensePlateOption)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  {LP_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                </div>
                <div>
                <label htmlFor="batchLpRear" className="block text-xs font-medium text-slate-600 mb-1">Plate (Rear)</label>
                <select
                  id="batchLpRear"
                  value={batchLpRear}
                  onChange={(e) => setBatchLpRear(e.target.value as LicensePlateOption)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  {LP_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                </div>
                {(batchLpFront === 'custom_plate' || batchLpRear === 'custom_plate' || hasCsvCustomPlate) && (
                  <div className="sm:col-span-2">
                    <label htmlFor="batchPlateName" className="block text-xs font-medium text-slate-600 mb-1">
                      Default custom plate
                    </label>
                    <select
                      id="batchPlateName"
                      value={batchPlateName}
                      onChange={(e) => setBatchPlateName(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                    >
                      <option value="">Select a stored plate</option>
                      {plateNames.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                    {plateNames.length === 0 && (
                      <p className="mt-1 text-[11px] text-amber-700">
                        Upload a plate image from Settings before using this mode.
                      </p>
                    )}
                    <p className="mt-1 text-[11px] text-slate-500">
                      Used when a custom-plate CSV row does not provide its own plate_name.
                    </p>
                  </div>
                )}
                <div>
                <label htmlFor="batchGenerationTier" className="block text-xs font-medium text-slate-600 mb-1">Generation tier</label>
                <select
                  id="batchGenerationTier"
                  aria-describedby="batch-generation-tier-help"
                  value={batchGenerationTier}
                  onChange={(e) => setBatchGenerationTier(e.target.value as GenerationTierOption)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  {GENERATION_TIER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p id="batch-generation-tier-help" className="mt-1.5 text-[11px] leading-4 text-slate-500">
                  {GENERATION_TIER_HELP}
                </p>
                </div>
              </div>

              {/* Batch high reflectance / metadata free toggles */}
              <div className="space-y-3">
                <label htmlFor="batchHighReflectance" className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    id="batchHighReflectance"
                    type="checkbox"
                    checked={batchHighReflectance}
                    onChange={(e) => setBatchHighReflectance(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-xs font-medium text-slate-600">
                    High reflectance (outdoor-style reflections)
                  </span>
                </label>

                <label htmlFor="batchMetadataFree" className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    id="batchMetadataFree"
                    type="checkbox"
                    checked={batchMetadataFree}
                    onChange={(e) => setBatchMetadataFree(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-xs font-medium text-slate-600">
                    Metadata free mode (rely on reference images only)
                  </span>
                </label>

                <label htmlFor="batchUseReferenceBank" className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    id="batchUseReferenceBank"
                    type="checkbox"
                    checked={batchUseReferenceBank}
                    onChange={(e) => setBatchUseReferenceBank(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-xs font-medium text-slate-600">
                    Use reference bank (when make, model, year and trim match an entry)
                  </span>
                </label>
              </div>
            </fieldset>
          )}

          {/* Submit batch button */}
          <button
            onClick={submitBatchScans}
            disabled={
              batchScanIds.length === 0
              || batchSubmitting
              || hasMissingBatchPlate
            }
            className="btn-primary w-full px-4 py-2.5 text-sm flex items-center justify-center gap-2"
          >
            {batchSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting batch…
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Submit Batch ({batchScanIds.length} scans)
              </>
            )}
          </button>

          <p className="sr-only" aria-live="polite" aria-atomic="true">
            {batchSubmitting
              ? `Submitting batch of ${batchScanIds.length} scans.`
              : batchResults.length > 0
                ? `Batch submission finished. ${successCount} submitted, ${errorCount} failed.`
                : ''}
          </p>

          {/* Batch results summary */}
          {batchResults.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-900">Submission Results</span>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-success-700">{successCount} submitted</span>
                  {errorCount > 0 && <span className="text-danger-700">{errorCount} failed</span>}
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-slate-200 rounded-full h-2 mb-3 overflow-hidden">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300"
                  style={{ width: `${(successCount / batchResults.length) * 100}%` }}
                />
              </div>

              {/* Show errors if any */}
              {errorCount > 0 && (
                <div className="max-h-24 overflow-y-auto space-y-1">
                  {batchResults
                    .filter(r => r.status === 'error')
                    .slice(0, 5)
                    .map((result, idx) => (
                      <div key={idx} className="text-xs text-danger-700 truncate">
                        {result.scan_id}: {result.error}
                      </div>
                    ))}
                </div>
              )}

              <Link
                to="/lightning/live"
                className="mt-4 block w-full px-4 py-2 rounded-lg border border-slate-200 bg-white text-center text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                View Progress in Live Scans
              </Link>
            </div>
          )}
        </div>
        </section>
      </div>
    </div>
  );
}
